import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Heart, ChatCircle, Trash, PencilSimple, ShoppingBag, UserCircle, Package, PaperPlaneRight, UploadSimple, CurrencyDollar, ShoppingCart, Receipt, Clock, CheckCircle, XCircle, Eye, Check } from "@phosphor-icons/react";
import BuyProductDialog from "@/components/BuyProductDialog";
import { useStripeReturn } from "@/lib/useStripeReturn";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString("es-MX");
}

function roleColor(r) {
  if (r === "admin") return "text-[#CCFF00]";
  if (r === "directivo") return "text-blue-400";
  if (r === "profesor") return "text-yellow-400";
  return "text-gray-400";
}

export default function Shop() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "directivo";
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [view, setView] = useState("catalog"); // catalog | orders
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", price: "", stock: "0" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentsByProduct, setCommentsByProduct] = useState({});
  const [newComment, setNewComment] = useState({});
  const [buyingProduct, setBuyingProduct] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [prods, ords, cls] = await Promise.all([
        api.get("/shop/products"),
        api.get("/shop/orders").catch(() => ({ data: [] })),
        api.get("/clubs").catch(() => ({ data: [] })),
      ]);
      setProducts(prods.data);
      setOrders(ords.data);
      setClubs(cls.data);
    } catch {
      toast.error("Error al cargar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useStripeReturn(() => load());

  const openCreate = () => {
    setEditingProduct(null);
    setForm({ name: "", description: "", price: "", stock: "0" });
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
    setCreateOpen(true);
  };

  const openEdit = (p) => {
    setEditingProduct(p);
    setForm({ name: p.name, description: p.description || "", price: String(p.price), stock: String(p.stock || 0) });
    setFile(null);
    setCreateOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    const price = parseFloat(form.price);
    if (isNaN(price) || price < 0) { toast.error("Precio invalido"); return; }
    setSaving(true);
    try {
      if (editingProduct) {
        await api.put(`/shop/products/${editingProduct.id}`, {
          name: form.name.trim(),
          description: form.description.trim(),
          price,
          stock: parseInt(form.stock) || 0,
        });
        toast.success("Producto actualizado");
      } else {
        if (!file) { toast.error("Sube una imagen"); setSaving(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("name", form.name.trim());
        fd.append("description", form.description.trim());
        fd.append("price", String(price));
        fd.append("stock", String(parseInt(form.stock) || 0));
        await api.post("/shop/products", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Producto creado");
      }
      setCreateOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminar producto?")) return;
    try {
      await api.delete(`/shop/products/${id}`);
      setProducts((p) => p.filter((x) => x.id !== id));
      toast.success("Eliminado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const toggleLike = async (p) => {
    try {
      const { data } = await api.post(`/shop/products/${p.id}/like`);
      setProducts((arr) => arr.map((x) => {
        if (x.id !== p.id) return x;
        const newLikes = data.liked ? [...(x.likes || []), user.id] : (x.likes || []).filter((u) => u !== user.id);
        return { ...x, likes: newLikes };
      }));
    } catch (err) { console.error("Product like error:", err); }
  };

  const loadComments = async (pid) => {
    try {
      const { data } = await api.get(`/shop/products/${pid}/comments`);
      setCommentsByProduct((m) => ({ ...m, [pid]: data }));
    } catch {
      toast.error("Error al cargar comentarios");
    }
  };

  const toggleComments = async (pid) => {
    const wasOpen = expandedComments[pid];
    setExpandedComments((m) => ({ ...m, [pid]: !wasOpen }));
    if (!wasOpen && !commentsByProduct[pid]) {
      await loadComments(pid);
    }
  };

  const submitComment = async (pid) => {
    const text = (newComment[pid] || "").trim();
    if (!text) return;
    try {
      await api.post(`/shop/products/${pid}/comments`, { text });
      setNewComment((m) => ({ ...m, [pid]: "" }));
      setProducts((arr) => arr.map((p) => p.id === pid ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p));
      await loadComments(pid);
    } catch {
      toast.error("Error al comentar");
    }
  };

  const deleteComment = async (pid, cid) => {
    if (!window.confirm("Eliminar comentario?")) return;
    try {
      await api.delete(`/shop/comments/${cid}`);
      setCommentsByProduct((m) => ({ ...m, [pid]: m[pid].filter((c) => c.id !== cid) }));
      setProducts((arr) => arr.map((p) => p.id === pid ? { ...p, comment_count: Math.max(0, (p.comment_count || 0) - 1) } : p));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="shop-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">{view === "catalog" ? "Catalogo" : (canManage ? "Administracion" : "Mis compras")}</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Tienda</h1>
          <p className="text-xs text-gray-500 mt-1 font-['JetBrains_Mono']">
            {view === "catalog" ? `${products.length} productos disponibles` : `${orders.length} pedidos`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex border border-white/20" data-testid="shop-view-toggle">
            <button
              onClick={() => setView("catalog")}
              className={`px-3 h-9 text-xs uppercase font-bold tracking-wider ${view === "catalog" ? "bg-[#CCFF00] text-black" : "bg-[#0A0A0A] text-gray-400 hover:text-[#CCFF00]"}`}
              data-testid="view-catalog"
            >
              <Package size={12} className="inline mr-1" /> Catalogo
            </button>
            <button
              onClick={() => setView("orders")}
              className={`px-3 h-9 text-xs uppercase font-bold tracking-wider ${view === "orders" ? "bg-[#CCFF00] text-black" : "bg-[#0A0A0A] text-gray-400 hover:text-[#CCFF00]"}`}
              data-testid="view-orders"
            >
              <Receipt size={12} className="inline mr-1" /> {canManage ? "Pedidos" : "Mis pedidos"}
              {orders.filter((o) => canManage ? o.payment_status === "en_revision" : o.payment_status !== "pagada").length > 0 && (
                <span className="ml-1 bg-red-500 text-white rounded-full px-1.5 text-[10px]">
                  {orders.filter((o) => canManage ? o.payment_status === "en_revision" : o.payment_status !== "pagada").length}
                </span>
              )}
            </button>
          </div>
          {canManage && view === "catalog" && (
            <Button onClick={openCreate} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs" data-testid="new-product-btn">
              <Plus size={14} className="mr-1" /> Nuevo Producto
            </Button>
          )}
        </div>
      </div>

      {view === "orders" ? (
        <OrdersList orders={orders} canManage={canManage} onChange={load} />
      ) : (products.length === 0 ? (
        <div className="card-tactical p-12 text-center">
          <ShoppingBag size={48} weight="duotone" className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">Aun no hay productos en el catalogo.</p>
          {canManage && <p className="text-xs text-gray-600 mt-2">Publica el primero con el boton de arriba.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.map((p) => {
            const liked = p.likes?.includes(user?.id);
            const comments = commentsByProduct[p.id] || [];
            const expanded = expandedComments[p.id];
            const imgUrl = `${BACKEND_URL}/api/shop/products/${p.id}/image`;
            return (
              <article key={p.id} className="card-tactical overflow-hidden flex flex-col" data-testid={`product-${p.id}`}>
                <div className="bg-[#0A0A0A] aspect-square flex items-center justify-center overflow-hidden">
                  <img src={imgUrl} alt={p.name} className="max-w-full max-h-full object-cover w-full h-full" data-testid={`product-img-${p.id}`} />
                </div>
                <div className="p-4 flex-1 flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white leading-tight">{p.name}</h3>
                    {canManage && (
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(p)} className="text-gray-400 hover:text-[#CCFF00] h-7 w-7" data-testid={`edit-product-${p.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(p.id)} className="text-gray-400 hover:text-red-400 h-7 w-7" data-testid={`delete-product-${p.id}`}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    )}
                  </div>
                  {p.description && <p className="text-sm text-gray-400 line-clamp-3">{p.description}</p>}
                  <div className="flex items-center justify-between mt-auto">
                    <span className="text-[#CCFF00] font-['JetBrains_Mono'] font-bold text-xl flex items-center">
                      <CurrencyDollar size={18} weight="fill" />{Number(p.price).toFixed(2)}
                    </span>
                    {p.stock > 0 ? (
                      <Badge variant="outline" className="badge-status bg-green-500/20 text-green-400 border-green-500/30">{p.stock} disponibles</Badge>
                    ) : (
                      <Badge variant="outline" className="badge-status bg-gray-500/20 text-gray-400 border-gray-500/30">Agotado</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 pt-2 border-t border-white/5">
                    <button onClick={() => toggleLike(p)} className={`flex items-center gap-1 text-sm transition-colors ${liked ? "text-pink-500" : "text-gray-400 hover:text-pink-400"}`} data-testid={`like-product-${p.id}`}>
                      <Heart size={18} weight={liked ? "fill" : "regular"} />
                      <span className="font-['JetBrains_Mono'] text-xs">{p.likes?.length || 0}</span>
                    </button>
                    <button onClick={() => toggleComments(p.id)} className="flex items-center gap-1 text-sm text-gray-400 hover:text-[#CCFF00]" data-testid={`toggle-comments-product-${p.id}`}>
                      <ChatCircle size={18} />
                      <span className="font-['JetBrains_Mono'] text-xs">{p.comment_count || 0}</span>
                    </button>
                    <Button
                      size="sm"
                      onClick={() => setBuyingProduct(p)}
                      disabled={!p.stock || p.stock < 1}
                      className="ml-auto bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs disabled:opacity-40"
                      data-testid={`buy-product-${p.id}`}
                    >
                      <ShoppingCart size={14} className="mr-1" /> Comprar
                    </Button>
                  </div>

                  {expanded && (
                    <div className="border-t border-white/5 pt-3 space-y-2" data-testid={`product-comments-${p.id}`}>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {comments.length === 0 ? (
                          <p className="text-xs text-gray-500 text-center py-2">Se el primero en comentar.</p>
                        ) : (
                          comments.map((c) => {
                            const canDeleteC = c.author_id === user?.id || canManage;
                            return (
                              <div key={c.id} className="flex items-start gap-2" data-testid={`product-comment-${c.id}`}>
                                {c.author_photo ? (
                                  <img src={c.author_photo} alt={c.author_name} className="w-6 h-6 rounded-full object-cover border border-white/10" />
                                ) : (
                                  <UserCircle size={24} weight="duotone" className="text-gray-600 shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5">
                                    <span className={`text-[11px] font-semibold ${roleColor(c.author_role)}`}>{c.author_name}</span>
                                    <p className="text-sm text-gray-200 whitespace-pre-wrap break-words">{c.text}</p>
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5 pl-1 text-[10px] text-gray-500">
                                    <span className="font-['JetBrains_Mono']">{timeAgo(c.created_at)}</span>
                                    {canDeleteC && <button onClick={() => deleteComment(p.id, c.id)} className="hover:text-red-400">Eliminar</button>}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Input
                          placeholder="Comenta..."
                          value={newComment[p.id] || ""}
                          onChange={(e) => setNewComment((m) => ({ ...m, [p.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitComment(p.id); } }}
                          className="bg-[#0A0A0A] border-white/20 text-white h-8 text-sm focus:border-[#CCFF00]"
                          data-testid={`product-comment-input-${p.id}`}
                        />
                        <Button size="sm" onClick={() => submitComment(p.id)} disabled={!(newComment[p.id] || "").trim()} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold text-xs h-8 disabled:opacity-40" data-testid={`product-comment-submit-${p.id}`}>
                          <PaperPlaneRight size={12} />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ))}

      <BuyProductDialog
        product={buyingProduct}
        clubs={clubs}
        open={!!buyingProduct}
        onOpenChange={(v) => { if (!v) setBuyingProduct(null); }}
        onSuccess={load}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">
              {editingProduct ? "Editar Producto" : "Nuevo Producto"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editingProduct && (
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Imagen</Label>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="text-sm text-gray-300 file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-[#CCFF00] file:text-black file:font-bold file:uppercase file:text-xs hover:file:bg-[#B3E600] cursor-pointer"
                  data-testid="product-file-input"
                />
                {file && <p className="text-[10px] text-gray-500 mt-1 font-['JetBrains_Mono']">{file.name} ({(file.size / 1024).toFixed(0)} KB)</p>}
              </div>
            )}
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" data-testid="product-name-input" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Descripcion</Label>
              <Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                rows={3} maxLength={2000}
                className="bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] resize-none"
                data-testid="product-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Precio (MXN)</Label>
                <Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                  className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" data-testid="product-price-input" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Stock</Label>
                <Input type="number" min="0" value={form.stock} onChange={(e) => setForm((p) => ({ ...p, stock: e.target.value }))}
                  className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" data-testid="product-stock-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={saving} className="text-gray-400 text-xs uppercase font-bold">Cancelar</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="product-submit-btn">
              {saving ? "Guardando..." : (editingProduct ? "Actualizar" : "Publicar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const STATUS_LABEL = {
  pendiente: { label: "Pendiente", color: "bg-orange-500/20 text-orange-400 border-orange-500/30", icon: Clock },
  en_revision: { label: "En revision", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: Eye },
  pagada: { label: "Pagada", color: "bg-green-500/20 text-green-400 border-green-500/30", icon: CheckCircle },
  rechazada: { label: "Rechazada", color: "bg-red-500/20 text-red-400 border-red-500/30", icon: XCircle },
};

function OrdersList({ orders, canManage, onChange }) {
  const approve = async (id) => {
    try { await api.post(`/shop/orders/${id}/approve`); toast.success("Pedido aprobado"); onChange && onChange(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Motivo de rechazo (opcional):") ?? "";
    try { await api.post(`/shop/orders/${id}/reject`, { reason }); toast.success("Pedido rechazado"); onChange && onChange(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const viewProof = async (id) => {
    try {
      const { data } = await api.get(`/shop/orders/${id}/proof`, { responseType: "blob" });
      window.open(window.URL.createObjectURL(data), "_blank");
    } catch (err) { toast.error(err.response?.status === 404 ? "Sin comprobante" : "Error"); }
  };
  const downloadReceipt = async (id) => {
    try {
      const { data } = await api.get(`/shop/orders/${id}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const removeOrder = async (id) => {
    if (!window.confirm("Eliminar pedido?")) return;
    try { await api.delete(`/shop/orders/${id}`); toast.success("Eliminado"); onChange && onChange(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (orders.length === 0) {
    return (
      <div className="card-tactical p-12 text-center" data-testid="empty-orders">
        <Receipt size={48} weight="duotone" className="text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400">No hay pedidos.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="orders-list">
      {orders.map((o) => {
        const s = STATUS_LABEL[o.payment_status] || STATUS_LABEL.pendiente;
        const SIcon = s.icon;
        return (
          <div key={o.id} className="card-tactical p-4" data-testid={`order-${o.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-['JetBrains_Mono'] text-xs text-gray-500">#{o.id.slice(0, 8)}</span>
                  <span className="text-sm text-white font-semibold">{o.user_name}</span>
                  <Badge variant="outline" className={`badge-status ${s.color} flex items-center gap-1`}>
                    <SIcon size={10} /> {s.label}
                  </Badge>
                </div>
                <p className="text-[11px] text-gray-500 mt-0.5 font-['JetBrains_Mono']">{new Date(o.created_at).toLocaleString("es-MX")}</p>
              </div>
              <span className="text-[#CCFF00] font-['JetBrains_Mono'] font-bold text-lg">${Number(o.total).toFixed(2)} MXN</span>
            </div>
            <div className="mt-2 text-sm text-gray-300 space-y-0.5">
              {o.items.map((it, idx) => (
                <div key={idx} className="flex justify-between">
                  <span>{it.quantity}x {it.name}</span>
                  <span className="font-['JetBrains_Mono'] text-gray-500">${Number(it.subtotal).toFixed(2)}</span>
                </div>
              ))}
            </div>
            {o.notes && <p className="text-xs text-gray-500 mt-2 italic">"{o.notes}"</p>}
            {o.rejection_reason && <p className="text-xs text-red-400 mt-2">Motivo rechazo: {o.rejection_reason}</p>}
            <div className="flex gap-2 mt-3 pt-3 border-t border-white/5 flex-wrap">
              {o.proof_path && (
                <Button size="sm" variant="outline" onClick={() => viewProof(o.id)} className="border-white/20 text-gray-300 hover:text-[#CCFF00] text-xs h-8" data-testid={`view-proof-${o.id}`}>
                  <Eye size={12} className="mr-1" /> Comprobante
                </Button>
              )}
              {o.payment_status === "pagada" && (
                <Button size="sm" variant="outline" onClick={() => downloadReceipt(o.id)} className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 text-xs h-8 uppercase font-bold" data-testid={`receipt-${o.id}`}>
                  <Receipt size={12} className="mr-1" /> Recibo PDF
                </Button>
              )}
              {canManage && o.payment_status === "en_revision" && (
                <>
                  <Button size="sm" onClick={() => approve(o.id)} className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30 text-xs h-8 uppercase font-bold" data-testid={`approve-${o.id}`}>
                    <Check size={12} className="mr-1" /> Aprobar
                  </Button>
                  <Button size="sm" onClick={() => reject(o.id)} className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 text-xs h-8 uppercase font-bold" data-testid={`reject-${o.id}`}>
                    <XCircle size={12} className="mr-1" /> Rechazar
                  </Button>
                </>
              )}
              {(canManage || o.payment_status !== "pagada") && (
                <Button size="sm" variant="ghost" onClick={() => removeOrder(o.id)} className="ml-auto text-gray-500 hover:text-red-400 text-xs h-8" data-testid={`delete-order-${o.id}`}>
                  <Trash size={12} />
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
